import apiClient from './client';
import type { PaginatedResponse } from '../types';

export interface EmployeeListItem {
  id: string;
  full_name: string;
  email: string;
  department: string;
  job_title: string;
  status: string;
  cv_count: number;
  skills: string[];
  created_at: string;
  last_cv_date: string | null;
  photo: string | null;
}

export interface EmployeeListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
}

// Maps frontend sort fields to JPA entity field names for standard columns.
const SORT_FIELD_MAP: Record<string, string> = {
  full_name: 'fullName',
  email: 'email',
  department: 'department',
  job_title: 'jobTitle',
  status: 'status',
};

// Sort fields handled by dedicated backend queries (not Spring Data sort).
const COMPUTED_SORT_MAP: Record<string, string> = {
  cv_count: 'cvCount',
  last_cv_date: 'lastCvDate',
};

export async function fetchEmployees(
  params: EmployeeListParams = {}
): Promise<PaginatedResponse<EmployeeListItem>> {
  const sortKey = params.sort_by ?? 'full_name';
  const order = params.order ?? 'asc';
  const computedSort = COMPUTED_SORT_MAP[sortKey];
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    ...(computedSort
      ? { sortBy: computedSort, sortOrder: order }
      : { sort: `${SORT_FIELD_MAP[sortKey] ?? 'fullName'},${order}` }),
    ...(params.search ? { q: params.search } : {}),
    ...(params.status ? { status: params.status } : {}),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>('/employees', { params: springParams });

  return {
    items: data.content ?? [],
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}

export interface EmployeeStats {
  total: number;
  with_cv: number;
  with_recent_cv: number;
}

export async function fetchEmployeeStats(): Promise<EmployeeStats> {
  const { data } = await apiClient.get<EmployeeStats>('/employees/stats');
  return data;
}

export async function fetchEmployee(id: string): Promise<EmployeeListItem> {
  const { data } = await apiClient.get(`/employees/${id}`);
  return data;
}

export interface EmployeeSkill {
  id: string;
  name: string;
  active: boolean;
}

/**
 * The taxonomy-resolved skills for an employee, aggregated across their CVs.
 * Each carries an `active` flag (false when removed everywhere).
 */
export async function fetchEmployeeSkills(employeeId: string): Promise<EmployeeSkill[]> {
  const { data } = await apiClient.get<EmployeeSkill[]>(`/employees/${employeeId}/skills`);
  return data;
}

/**
 * Remove (`active=false`, with a `reason`) or add back (`active=true`) a
 * taxonomy-resolved skill across all the employee's CVs. `reason` is the
 * deactivation controlled vocabulary (`not_required` / `not_a_skill` /
 * `not_present`) and is only sent on a removal. Returns the updated skill, or
 * `null` if the employee has no such skill (404).
 */
export async function setEmployeeSkillActive(
  employeeId: string,
  skillId: string,
  active: boolean,
  reason?: string,
): Promise<EmployeeSkill | null> {
  const response = await apiClient.patch<EmployeeSkill>(
    `/employees/${employeeId}/skills/${skillId}`,
    active ? { active } : { active, reason },
    { validateStatus: (status) => status === 200 || status === 404 },
  );
  if (response.status === 404) return null;
  return response.data;
}

export interface EntraSyncResult {
  fetched: number;
  created: number;
  updated: number;
  skipped: number;
  deactivated: number;
}

/**
 * Trigger a full employee sync from Microsoft Entra ID.
 */
export async function syncEmployeesFromEntra(): Promise<EntraSyncResult> {
  const { data } = await apiClient.post<EntraSyncResult>('/employees/sync-entra');
  return data;
}
